import React, { useEffect, useRef, useState } from 'react';
import { useAddressAutocomplete } from 'hooks/useAddressAutocomplete';
import { formatPhoneInput } from 'utils/phoneFormat';

/**
 * Street address (with Google Places autocomplete) + apt/suite + city/state/zip
 * + phone + delivery notes — the slice of a delivery form shared by guest
 * checkout (CheckoutPage) and a signed-in customer's saved address
 * (AccountPanel's Address screen). City/state/zip are read-only: they're only
 * ever set by picking a suggestion, never hand-typed.
 *
 * `recipientName`/`orderNotes` are order-specific and stay in CheckoutPage —
 * pass them (or anything else) as `children` to render above the address.
 *
 * @param {{address, address2, city, state, zip, phone, deliveryNotes}} value
 * @param {(patch: object) => void} onChange shallow-merge setter
 * @param {string} idPrefix e.g. "checkout" or "account-address" — becomes each
 *   field's data-testid as `${idPrefix}-<field>`
 */
export default function DeliveryAddressFields({ value, onChange, idPrefix, showNotes = true, children }) {
  const {
    suggestions: addressSuggestions,
    isSearching: addressSearching,
    fetchSuggestions,
    clearSuggestions,
    resolveSuggestion,
  } = useAddressAutocomplete();
  const [addressSuggestionsOpen, setAddressSuggestionsOpen] = useState(false);
  const addressDebounceRef = useRef(null);
  useEffect(() => () => clearTimeout(addressDebounceRef.current), []);

  const handleAddressChange = (e) => {
    const input = e.target.value;
    if (input.trim() === '') {
      // City/state/zip are read-only and only ever set by picking a
      // suggestion, so clear them along with the address instead of leaving
      // stale values behind once there's no address text to justify them.
      onChange({ address: input, city: '', state: '', zip: '' });
    } else {
      onChange({ address: input });
    }
    setAddressSuggestionsOpen(true);
    clearTimeout(addressDebounceRef.current);
    addressDebounceRef.current = setTimeout(() => fetchSuggestions(input), 300);
  };

  const handleSelectAddressSuggestion = async (suggestion) => {
    const parsed = await resolveSuggestion(suggestion);
    onChange({
      address: parsed.address || value.address,
      address2: parsed.address2 || value.address2,
      city: parsed.city || value.city,
      state: parsed.state || value.state,
      zip: parsed.zip || value.zip,
    });
    setAddressSuggestionsOpen(false);
  };

  const testid = (field) => `${idPrefix}-${field}`;

  return (
    <>
      {children}

      <div className="sf-address-autocomplete">
        <svg
          className="sf-address-autocomplete__icon"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 22s7-7.16 7-12.5A7 7 0 0 0 5 9.5C5 14.84 12 22 12 22Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="9.5" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        </svg>
        <input
          className="sf-input sf-address-autocomplete__input"
          placeholder="Start typing your street address…"
          value={value.address || ''}
          onChange={handleAddressChange}
          onFocus={() => setAddressSuggestionsOpen(true)}
          onBlur={() => setAddressSuggestionsOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              clearSuggestions();
              setAddressSuggestionsOpen(false);
            }
          }}
          autoComplete="off"
          required
          data-testid={testid('address')}
        />
        {addressSearching && <span className="sf-address-autocomplete__spinner" aria-hidden="true" />}
        {addressSuggestionsOpen && addressSuggestions.length > 0 && (
          <ul className="sf-address-autocomplete__list">
            {addressSuggestions.map((suggestion) => {
              const prediction = suggestion.placePrediction;
              const primaryText = prediction.mainText?.text || prediction.text?.text || '';
              const secondaryText = prediction.secondaryText?.text || '';
              return (
                <li key={prediction.placeId}>
                  <button
                    type="button"
                    className="sf-address-autocomplete__item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelectAddressSuggestion(suggestion)}
                    data-testid={testid('address-suggestion')}
                  >
                    <span className="sf-address-autocomplete__primary">{primaryText}</span>
                    {secondaryText && (
                      <span className="sf-address-autocomplete__secondary">{secondaryText}</span>
                    )}
                  </button>
                </li>
              );
            })}
            <li className="sf-address-autocomplete__attribution">Powered by Google</li>
          </ul>
        )}
      </div>

      <div className="sf-address-line2">
        <input
          className="sf-input"
          placeholder="Apt, suite, unit, etc. (optional)"
          value={value.address2 || ''}
          onChange={(e) => onChange({ address2: e.target.value })}
          autoComplete="address-line2"
          data-testid={testid('address2')}
        />
      </div>

      <div className="sf-address-group">
        <input
          className="sf-input"
          placeholder="City"
          value={value.city || ''}
          onChange={(e) => onChange({ city: e.target.value })}
          readOnly
          required
          data-testid={testid('city')}
        />
        <input
          className="sf-input"
          placeholder="State"
          value={value.state || ''}
          onChange={(e) => onChange({ state: e.target.value })}
          readOnly
          required
          data-testid={testid('state')}
        />
        <input
          className="sf-input"
          placeholder="Zip"
          value={value.zip || ''}
          onChange={(e) => onChange({ zip: e.target.value })}
          readOnly
          required
          data-testid={testid('zip')}
        />
      </div>

      <div className="sf-address-phone">
        <input
          className="sf-input"
          placeholder="Phone"
          value={value.phone || ''}
          onChange={(e) => onChange({ phone: formatPhoneInput(e.target.value) })}
          required
          autoComplete="tel"
          inputMode="tel"
          data-testid={testid('phone')}
        />
      </div>

      {showNotes && <div className="sf-checkout__notes">
        <label className="sf-checkout__notes-label" htmlFor={testid('delivery-notes')}>
          Delivery instructions
        </label>
        <textarea
          id={testid('delivery-notes')}
          className="sf-input sf-textarea"
          placeholder="Where should we leave your meals if you're not home during delivery?"
          value={value.deliveryNotes || ''}
          onChange={(e) => onChange({ deliveryNotes: e.target.value })}
          rows={3}
          maxLength={1000}
          data-testid={testid('delivery-notes')}
        />
      </div>}
    </>
  );
}
